import { useState, useEffect } from 'react';
import { getAllUsers, getAllOrders, getAllItems, deleteUser, deleteOrder, deleteProduct, getSiteSettingsAdmin, updateSiteSettings, getMarketplaceEconomicsAdmin, updateMarketplaceEconomicsAdmin, getUserById, createUser, updateUser, addStrike, removeStrike, banUser, unbanUser, setUserEmailConfirmed, resendUserEmailConfirmation, createOrderAdmin, createProductAdmin, getAdminOrderById, getProductById, updateOrderAdmin, updateProductAdmin, createSpecification, deleteSpecification, getAllAdminCategories, getAllSpecifications, updateSpecification, createCategory, deleteCategory, updateCategory, AdmincreateChecklistTemplateAdmin, AdmindeleteChecklistTemplate, getAllAdminChecklistTemplates, AdminupdateChecklistTemplateAdmin, AdminSyncChecklists, getAdminChecklistTemplateById, getAllAdminChecklistPhases, updateChecklistPhaseIcon, resolveReport, AdminSubscribeShopifyWebhooks, getAllLanguagesAdmin, createLanguageAdmin, deleteLanguageAdmin, updateLanguageAdmin, getAllAdminSidebarFilters, getAdminSidebarFilterById, createAdminSidebarFilter, updateAdminSidebarFilter, deleteAdminSidebarFilter, syncAdminSidebarFilterOptions, getAllPartnerInquiries, getAllAdminTickets, updateAdminTicketStatus } from '../../services/api.admin';
import { SiteSettings, MarketplaceEconomicsDto, AdminUserDetailDto, StrikeDto, AdminCreateUserDto, AdminUpdateUserDto, AdminUserListDto, AdminCreateOrderDto, AdminCreateProductDto, AdminOrderDetailDto, AdminProductDetailDto, AdminUpdateOrderDto, AdminUpdateProductDto, ProductCondition, OrderStatus, PaymentStatus, ProductStatus, CategoryDto, CreateCategoryDto, UpdateCategoryDto, CreateSpecificationDto, SpecificationDto, UpdateSpecificationDto, SpecificationType, AdminProductListDto, AdminOrderListDto, AccountStatus, AdminChecklistTemplate, AdminCreateChecklistTemplateDto, AdminUpdateChecklistTemplateDto, AdminChecklistPhaseDto, LanguageAdminDto, AdminSidebarFilterListDto, SidebarFilterDto, UpdateSidebarFilterDto, SidebarFilterType, PartnerInquiryListDto, AdminTicketListDto } from '../../types';
import AdminHeader from '../components/AdminHeader';
import AdminSidebar from '../components/AdminSidebar';
import DataTable from '../components/DataTable';
import SiteSettingsSection from '../components/SiteSettingsSection';
import MarketplaceEconomicsSection from '../components/MarketplaceEconomicsSection';
import PickupLocationsSection from '../components/PickupLocationsSection';
import UserModal from '../components/UserModal';
import OrderModal from '../components/OrderModal';
import ProductModal from '../components/ProductModal';
import SpecificationModal from '../components/SpecificationModal';
import CategoryModal from '../components/CategoryModal';
import ChecklistModal from '../components/ChecklistModal';
import ChecklistPhaseIconModal from '../components/ChecklistPhaseIconModal';
import SidebarFilterModal from '../components/SidebarFilterModal';
import TranslationModal from '../components/TranslationModal';
import {
    // ... your existing ones
    TranslationAdminDto,
    CreateTranslationDto,
    UpdateTranslationDto
} from '../../types';

// Translations (already defined in api.admin)
import { getAllAdminTranslations, createTranslationAdmin, updateTranslationAdmin, deleteTranslationAdmin } from '../../services/api.admin';
import SetupPage from '../components/SetupPage';
import EmailTemplatesPanel from '../components/EmailTemplatesPanel';
import MyRebabyDesignerPanel from '../components/MyRebabyDesignerPanel';
import MetaAdCatalogPanel from '../components/MetaAdCatalogPanel';
import LoginPromptsSection from '../components/LoginPromptsSection';

type View = 'users' | 'orders' | 'products' | 'settings' | 'login-prompts' | 'marketplace-economics' | 'pickup-locations' | 'categories' | 'specifications' | 'sidebar-filters' | 'checklists' | 'partner-inquiries' | 'tickets' | 'status' | 'translations' | 'setup' | 'email-templates' | 'myrebaby-designer' | 'meta-ad-catalog';

const getAdditionalUserFields = () => {
    const listDto: AdminUserListDto = {
        id: '',
        email: '',
        userName: '',
        isAdmin: false,
        roles: [],
        status: AccountStatus.OK,
        strikeCount: 0,
        isFlagged: false,
        hasProvidedTaxInfo: false,
    };

    const detailDto: AdminUserDetailDto = {
        ...listDto,
        createdDate: '',
        lockoutEndDate: undefined,
        twoFactorEnabled: false,
        emailConfirmed: false,
        strikes: [],
        lockoutEnabled: false,
        phoneNumber: '',
        streetAddress: '',
        city: '',
        postalCode: '',
        country: '',
        isFlagged: false,
        itemsSoldThisYear: 0,
        totalEuroThisYear: 0,
        taxId: '', // New
        hasProvidedTaxInfo: false, // New
        reports: [],
    };

    const listKeys = Object.keys(listDto);
    return Object.keys(detailDto).filter(key => !listKeys.includes(key));
};

// Similarly for products (add to code)
const getAdditionalProductFields = () => {
    const listDto: AdminProductListDto = {
        id: 0,
        title: '',
        price: 0,
        sellerId: undefined,
        isSold: false,
        status: ProductStatus.PendingReview,
        condition: ProductCondition.New,
        viewCount: 0,
    };

    const detailDto: AdminProductDetailDto = {
        ...listDto,
        description: '',
        imageUrls: [],
        quantityAvailable: 0,
        tags: [],
        averageRating: 0,
        reviewCount: 0,
        createdAt: '',
        updatedAt: undefined,
        categories: [],
        specifications: {},
    };

    const listKeys = Object.keys(listDto);
    return Object.keys(detailDto).filter(key => !listKeys.includes(key));
};

// Similarly for orders (add to code)
const getAdditionalOrderFields = () => {
    const listDto: AdminOrderListDto = {
        id: 0,
        buyerId: '',
        productId: 0,
        shopifyOrderId: '',
        status: OrderStatus.Pending,
        total: 0,
        isDisputed: false,
    };

    const detailDto: AdminOrderDetailDto = {
        ...listDto,
        orderDate: '',
        shippedDate: undefined,
        paymentMethod: undefined,
        paymentStatus: PaymentStatus.Pending,
        shippingCost: 0,
        shippingMethod: undefined,
        trackingNumber: undefined,
        adminNotes: undefined,
        shippingStreetAddress: undefined,
        shippingCity: undefined,
        shippingPostalCode: undefined,
        shippingCountry: undefined,
    };

    const listKeys = Object.keys(listDto);
    return Object.keys(detailDto).filter(key => !listKeys.includes(key));
};

const AdminDashboard = () => {
    const [currentView, setCurrentView] = useState<View>('setup');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [users, setUsers] = useState<AdminUserListDto[] | null>(null);
    const [orders, setOrders] = useState<AdminOrderListDto[] | null>(null);
    const [products, setProducts] = useState<AdminProductListDto[] | null>(null);
    const [settings, setSettings] = useState<SiteSettings | null>(null);
    const [economics, setEconomics] = useState<MarketplaceEconomicsDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingField, setEditingField] = useState<'textColor' | 'backgroundColor' | 'accentColor' | 'highlightColor' | null>(null);
    const [tempColor, setTempColor] = useState<string>('');
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    const [isUserLoading, setIsUserLoading] = useState(false);
    const [isOrderLoading, setIsOrderLoading] = useState(false);
    const [isProductLoading, setIsProductLoading] = useState(false);

    const additionalUserFields = getAdditionalUserFields();
    const additionalProductFields = getAdditionalProductFields(); // Add for products
    const additionalOrderFields = getAdditionalOrderFields(); // Add for orders

    // New state for user management
    const [selectedUser, setSelectedUser] = useState<AdminUserDetailDto | null>(null);
    const [showUserForm, setShowUserForm] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createFormData, setCreateFormData] = useState<AdminCreateUserDto>({
        email: '',
        userName: '',
        password: '',
        isAdmin: false,
        roles: [],
        // New fields
        lockoutEnabled: false,
        twoFactorEnabled: false,
        phoneNumber: '',
        streetAddress: '',
        city: '',
        postalCode: '',
        country: ''
    });
    const [updateFormData, setUpdateFormData] = useState<AdminUpdateUserDto>({
        email: '',
        userName: '',
        isAdmin: false,
        roles: [],
        lockoutEnabled: false,
        // New fields
        twoFactorEnabled: false,
        lockoutEnd: '',
        phoneNumber: '',
        streetAddress: '',
        city: '',
        postalCode: '',
        country: '',
        hasProvidedTaxInfo: false,
        isFlagged: false,
        taxId: ''
    });
    const [newStrikeReason, setNewStrikeReason] = useState('');

    // New: Product states
    const [selectedProduct, setSelectedProduct] = useState<AdminProductDetailDto | null>(null);
    const [showProductForm, setShowProductForm] = useState(false);
    const [isCreatingProduct, setIsCreatingProduct] = useState(false);
    const [createProductFormData, setCreateProductFormData] = useState<AdminCreateProductDto>({
        title: '',
        price: 0,
        description: '',
        imageUrls: [],
        sellerId: '', // Will be selected from dropdown
        condition: ProductCondition.LikeNew,
        quantityAvailable: 1,
        categories: [],
        tags: [],
        specifications: {}
    });
    const [updateProductFormData, setUpdateProductFormData] = useState<AdminUpdateProductDto>({
        title: '',
        price: 0,
        description: '',
        imageUrls: [],
        sellerId: '',
        isSold: false,
        condition: ProductCondition.LikeNew,
        status: ProductStatus.PendingReview,
        quantityAvailable: 1,
        categories: [],
        tags: [],
        specifications: {}
    });

    // New: Order states
    const [selectedOrder, setSelectedOrder] = useState<AdminOrderDetailDto | null>(null);
    const [showOrderForm, setShowOrderForm] = useState(false);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [createOrderFormData, setCreateOrderFormData] = useState<AdminCreateOrderDto>({
        buyerId: '', // Will be selected from dropdown
        productId: 0,
        shopifyOrderId: '',
        total: 0,
        status: OrderStatus.Pending,
        paymentMethod: '',
        paymentStatus: PaymentStatus.Pending,
        shippingCost: 0,
        shippingMethod: '',
        trackingNumber: '',
        isDisputed: false,
        adminNotes: '',
        shippingStreetAddress: '',
        shippingCity: '',
        shippingPostalCode: '',
        shippingCountry: ''
    });
    const [updateOrderFormData, setUpdateOrderFormData] = useState<AdminUpdateOrderDto>({
        shopifyOrderId: '',
        total: 0,
        status: OrderStatus.Pending,
        shippedDate: '',
        paymentMethod: '',
        paymentStatus: PaymentStatus.Pending,
        shippingCost: 0,
        shippingMethod: '',
        trackingNumber: '',
        isDisputed: false,
        adminNotes: '',
        shippingStreetAddress: '',
        shippingCity: '',
        shippingPostalCode: '',
        shippingCountry: ''
    });

    //new specification states
    const [specificationsList, setSpecificationsList] = useState<SpecificationDto[] | null>(null);
    const [selectedSpec, setSelectedSpec] = useState<SpecificationDto | null>(null);
    const [showSpecForm, setShowSpecForm] = useState(false);
    const [isCreatingSpec, setIsCreatingSpec] = useState(false);
    const [createSpecFormData, setCreateSpecFormData] = useState<CreateSpecificationDto>({
        name: '',
        type: SpecificationType.String,
        isRequired: false,
        options: '',
    });
    const [updateSpecFormData, setUpdateSpecFormData] = useState<UpdateSpecificationDto>({
        name: '',
        type: SpecificationType.String,
        isRequired: false,
        options: '',
    });

    const [sidebarFiltersList, setSidebarFiltersList] = useState<AdminSidebarFilterListDto[] | null>(null);
    const [editingSidebarFilter, setEditingSidebarFilter] = useState<SidebarFilterDto | null>(null);
    const [showCreateSidebarFilter, setShowCreateSidebarFilter] = useState(false);
    const [newSidebarFilterSpecId, setNewSidebarFilterSpecId] = useState<number | ''>('');

    // New: Category states (mirror specifications)
    const [categoriesList, setCategoriesList] = useState<CategoryDto[] | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<CategoryDto | null>(null);
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [createCategoryFormData, setCreateCategoryFormData] = useState<CreateCategoryDto>({
        name: ''
        // Add description if needed
    });
    const [updateCategoryFormData, setUpdateCategoryFormData] = useState<UpdateCategoryDto>({
        name: ''
        // Add description if needed
    });
    const [allCategories, setAllCategories] = useState<CategoryDto[]>([]); // For assigning in modals (specs, etc.)

    //New: Checklist states
    const [checklistsList, setChecklistsList] = useState<AdminChecklistTemplate[] | null>(null);
    const [selectedChecklist, setSelectedChecklist] = useState<AdminChecklistTemplate | null>(null);
    const [showChecklistForm, setShowChecklistForm] = useState(false);
    const [isCreatingChecklist, setIsCreatingChecklist] = useState(false);
    const [createChecklistFormData, setCreateChecklistFormData] = useState<AdminCreateChecklistTemplateDto>({
        names: {},
        phaseIds: [],
    });
    const [updateChecklistFormData, setUpdateChecklistFormData] = useState<AdminUpdateChecklistTemplateDto>({
        names: {},
        phaseIds: [],
    });
    const [syncChecklistsLoading, setSyncChecklistsLoading] = useState(false);
    const [checklistPhases, setChecklistPhases] = useState<AdminChecklistPhaseDto[] | null>(null);
    const [editingPhase, setEditingPhase] = useState<AdminChecklistPhaseDto | null>(null);
    const [partnerInquiries, setPartnerInquiries] = useState<PartnerInquiryListDto[] | null>(null);
    const [tickets, setTickets] = useState<AdminTicketListDto[] | null>(null);
    const [ticketStatusUpdatingId, setTicketStatusUpdatingId] = useState<number | null>(null);

    // New: Shopify webhook management
    const [shopifyStatus, setShopifyStatus] = useState<Record<string, boolean> | null>(null);
    const [shopifyLoading, setShopifyLoading] = useState(false);
    const [lastChecked, setLastChecked] = useState<string | null>(null);

    //new state for translations mananagement
    // New: Translation states
    const [languages, setLanguages] = useState<LanguageAdminDto[]>([]);
    const [translationsList, setTranslationsList] = useState<TranslationAdminDto[] | null>(null);
    const [selectedTranslation, setSelectedTranslation] = useState<TranslationAdminDto | null>(null);
    const [showTranslationForm, setShowTranslationForm] = useState(false);
    const [isCreatingTranslation, setIsCreatingTranslation] = useState(false);
    const [createTranslationFormData, setCreateTranslationFormData] = useState<CreateTranslationDto>({
        key: '',
        description: '',
        translations: {}          // ← NEW
    });
    const [updateTranslationFormData, setUpdateTranslationFormData] = useState<UpdateTranslationDto>({
        description: '',
        translations: {}          // ← NEW
    });

    // Language states
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageAdminDto | null>(null);
    const [isCreatingLanguage, setIsCreatingLanguage] = useState(false);
    const [languageFormData, setLanguageFormData] = useState({
        code: '',
        name: '',
        nativeName: '',
        flagEmoji: '',
        isActive: true,
        sortOrder: 0,
        translationKey: ''
    });

    // Load languages once when admin dashboard mounts
    useEffect(() => {
        const loadLanguagesOnce = async () => {
            if (languages.length === 0) {
                try {
                    const langs = await getAllLanguagesAdmin();
                    setLanguages(Array.isArray(langs) ? langs : []);
                } catch (err) {
                    console.error('Failed to load languages', err);
                    setLanguages([]);
                }
            }
        };

        loadLanguagesOnce();
    }, []); // ← Empty array = runs only once on mount


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                if (currentView === 'status') {
                    handleSubscribeWebhooks();   // This already fetches and shows the status
                }
                if (currentView === 'users' && users === null) {
                    const usersData = await getAllUsers();
                    setUsers(Array.isArray(usersData) ? usersData : []);
                } else if (currentView === 'orders' && orders === null) {
                    const ordersData = await getAllOrders();
                    setOrders(Array.isArray(ordersData) ? ordersData : []);
                } else if (currentView === 'products' && products === null) {
                    const productsData = await getAllItems();
                    setProducts(Array.isArray(productsData) ? productsData : []);
                } else if (currentView === 'settings' && settings === null) {
                    const siteSettings = await getSiteSettingsAdmin();
                    setSettings(siteSettings);
                } else if (currentView === 'marketplace-economics' && economics === null) {
                    const eco = await getMarketplaceEconomicsAdmin();
                    setEconomics(eco);
                } else if (currentView === 'specifications' && specificationsList === null) {
                    const specsData = await getAllSpecifications();
                    setSpecificationsList(Array.isArray(specsData) ? specsData : []);
                    const cats = await getAllAdminCategories();
                    setAllCategories(Array.isArray(cats) ? cats : []); // Empty array check
                } else if (currentView === 'sidebar-filters' && sidebarFiltersList === null) {
                    const filtersData = await getAllAdminSidebarFilters();
                    setSidebarFiltersList(Array.isArray(filtersData) ? filtersData : []);
                    if (specificationsList === null) {
                        const specsData = await getAllSpecifications();
                        setSpecificationsList(Array.isArray(specsData) ? specsData : []);
                    }
                } else if (currentView === 'categories' && categoriesList === null) {
                    const catsData = await getAllAdminCategories();
                    setCategoriesList(Array.isArray(catsData) ? catsData : []);
                    setAllCategories(Array.isArray(catsData) ? catsData : []); // Update allCategories as well if needed
                } else if (currentView === 'checklists' && checklistsList === null) {
                    const checklistsData = await getAllAdminChecklistTemplates();
                    setChecklistsList(Array.isArray(checklistsData) ? checklistsData : []);
                    const phasesData = await getAllAdminChecklistPhases();
                    setChecklistPhases(Array.isArray(phasesData) ? phasesData : []);
                } else if (currentView === 'partner-inquiries' && partnerInquiries === null) {
                    const inquiriesData = await getAllPartnerInquiries();
                    setPartnerInquiries(Array.isArray(inquiriesData) ? inquiriesData : []);
                    // Load categories if not already set (for dropdown in modal)
                    if (allCategories.length === 0) {
                        const cats = await getAllAdminCategories();
                        setAllCategories(Array.isArray(cats) ? cats : []);
                    }
                } else if (currentView === 'tickets' && tickets === null) {
                    const ticketsData = await getAllAdminTickets();
                    setTickets(Array.isArray(ticketsData) ? ticketsData : []);
                } else if (currentView === 'translations' && translationsList === null) {
                    const translationsData = await getAllAdminTranslations();
                    setTranslationsList(Array.isArray(translationsData) ? translationsData : []);
                } else if (currentView === 'setup' && languages.length === 0) {
                    const langs = await getAllLanguagesAdmin();
                    setLanguages(Array.isArray(langs) ? langs : []);
                }

            } catch (err) {
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentView, users, orders, products, settings, specificationsList, categoriesList]); // Added categoriesList

    const handleDelete = async (type: 'user' | 'order' | 'product', id: string | number) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            if (type === 'user') await deleteUser(id as string);
            if (type === 'order') await deleteOrder(id as number);
            if (type === 'product') await deleteProduct(id as number);
            if (type === 'user') setUsers((prev) => prev ? prev.filter(u => u.id !== id) : null);
            if (type === 'order') setOrders((prev) => prev ? prev.filter(o => o.id !== id) : null);
            // Soft-archive may leave the row (Status=Archived) — remove from list either way
            if (type === 'product') setProducts((prev) => prev ? prev.filter(p => p.id !== id) : null);
        } catch (err) {
            console.error('Delete failed', err);
            const axiosErr = err as { response?: { data?: { message?: string; errors?: { General?: string } } } };
            const msg =
                axiosErr.response?.data?.message ||
                axiosErr.response?.data?.errors?.General ||
                'Delete failed';
            alert(msg);
        }
    };

    const handleEditUser = async (id: string) => {
        if (!id) {
            console.error('Cannot edit user: ID is undefined');
            return;
        }
        console.log('Editing user with ID:', id);
        const userFromList = users?.find(u => u.id === id);
        if (userFromList) {
            setUpdateFormData({
                email: userFromList.email,
                userName: userFromList.userName,
                isAdmin: userFromList.isAdmin, // Now setting isAdmin
                roles: userFromList.roles,
                lockoutEnabled: false, // Placeholder
                // New placeholders
                twoFactorEnabled: false,
                lockoutEnd: '',
                phoneNumber: '',
                streetAddress: '',
                city: '',
                postalCode: '',
                country: '',
                hasProvidedTaxInfo: false,
                isFlagged: false,
                taxId: '',
            });

            // Force sync isAdmin with roles right after initial setUpdateFormData (extra safety on load)
            setUpdateFormData(prev => ({
                ...prev,
                isAdmin: (prev.roles || []).includes('Admin')
            }));

            setSelectedUser({
                ...userFromList,
                createdDate: '',
                lockoutEndDate: undefined,
                twoFactorEnabled: false,
                emailConfirmed: false,
                strikes: [], // Placeholder until full fetch
                lockoutEnabled: false, // New
                phoneNumber: '', // New
                streetAddress: '', // New
                city: '', // New
                postalCode: '', // New
                country: '', // New
                isFlagged: false, // New default
                itemsSoldThisYear: 0, // New: Fix TS error
                totalEuroThisYear: 0, // New: Fix TS error
                annualStats: [], // New: If optional, but add empty array
                taxId: '', // New
                hasProvidedTaxInfo: false,
                reports: [],
            });
            setIsCreating(false);
            setShowUserForm(true);
            setIsUserLoading(true); // Start loading
            // Async fetch full details

            try {
                const userData = await getUserById(id);
                setSelectedUser(userData);
                setUpdateFormData({
                    email: userData.email,
                    userName: userData.userName,
                    isAdmin: userData.isAdmin,
                    roles: userData.roles,
                    lockoutEnabled: userData.lockoutEnabled,
                    twoFactorEnabled: userData.twoFactorEnabled,
                    lockoutEnd: userData.lockoutEndDate ?? undefined,
                    phoneNumber: userData.phoneNumber ?? '',
                    streetAddress: userData.streetAddress ?? '',
                    city: userData.city ?? '',
                    postalCode: userData.postalCode ?? '',
                    country: userData.country ?? '',
                    taxId: userData.taxId ?? '',
                    hasProvidedTaxInfo: userData.hasProvidedTaxInfo,
                    isFlagged: userData.isFlagged,
                });

                // Force sync isAdmin with roles right after setUpdateFormData (extra safety on load)
                setUpdateFormData(prev => ({
                    ...prev,
                    isAdmin: (prev.roles || []).includes('Admin')
                }));
            } catch (err) {
                alert('Failed to load full user details');
            } finally {
                setIsUserLoading(false); // Update modal prop if using state
            }
        }
    };

    const handleCreateUser = () => {
        setSelectedUser(null);
        setCreateFormData({
            email: '',
            userName: '',
            password: '',
            isAdmin: false,
            roles: [],
            // New
            lockoutEnabled: false,
            twoFactorEnabled: false,
            phoneNumber: '',
            streetAddress: '',
            city: '',
            postalCode: '',
            country: ''
        });
        setIsCreating(true);
        setShowUserForm(true);
    };

    const handleSaveUser = async () => {
        if (isCreating) {
            await createUser(createFormData);
        } else if (selectedUser) {
            await updateUser(selectedUser.id, updateFormData);
        }
        setShowUserForm(false);
        // Refresh users list
        const usersData = await getAllUsers();
        setUsers(Array.isArray(usersData) ? usersData : []);
    };

    const handleAddStrike = async () => {
        if (!selectedUser || !newStrikeReason.trim()) return;
        try {
            const strikeDto: StrikeDto = { reason: newStrikeReason } as StrikeDto; // Type assertion if id/created not needed
            await addStrike(selectedUser.id, strikeDto);
            setNewStrikeReason('');
            // Refresh user data
            const updatedUser = await getUserById(selectedUser.id);
            setSelectedUser(updatedUser);
            const usersData = await getAllUsers();
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (err) {
            alert('Add strike failed');
        }
    };

    const handleRemoveStrike = async (strikeId: string) => {
        if (!selectedUser) return;
        if (!window.confirm('Remove this strike?')) return;
        try {
            await removeStrike(selectedUser.id, strikeId);
            // Refresh
            const updatedUser = await getUserById(selectedUser.id);
            setSelectedUser(updatedUser);
        } catch (err) {
            alert('Remove strike failed');
        }
    };

    const handleBanUser = async () => {
        if (!selectedUser || !window.confirm('Ban this user?')) return;
        const reason = prompt('Enter ban reason (optional):') || '';
        try {
            await banUser(selectedUser.id, reason);
            setShowUserForm(false);
            const usersData = await getAllUsers();
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (err) {
            alert('Ban failed');
        }
    };

    const handleUnbanUser = async () => {
        if (!selectedUser || !window.confirm('Unban this user?')) return;
        try {
            await unbanUser(selectedUser.id);
            const updatedUser = await getUserById(selectedUser.id);
            setSelectedUser(updatedUser);
        } catch (err) {
            alert('Unban failed');
        }
    };

    /** Shop login requires EmailConfirmed — admin can toggle to bypass or re-test verification. */
    const handleSetEmailConfirmed = async (confirmed: boolean) => {
        if (!selectedUser) return;
        await setUserEmailConfirmed(selectedUser.id, confirmed);
        const updatedUser = await getUserById(selectedUser.id);
        setSelectedUser(updatedUser);
        const usersData = await getAllUsers();
        setUsers(Array.isArray(usersData) ? usersData : []);
    };

    const handleResendEmailConfirmation = async () => {
        if (!selectedUser) return;
        await resendUserEmailConfirmation(selectedUser.id);
        alert('Confirmation email enqueued (if still unconfirmed).');
    };

    //reports
    const handleResolveReport = async (reportId: string) => {
        if (!selectedUser) return;
        if (!window.confirm('Mark this report as Reviewed?')) return;

        try {
            await resolveReport(reportId);           // ← we'll add this api call next
            // Refresh user data
            const updatedUser = await getUserById(selectedUser.id);
            setSelectedUser(updatedUser);
        } catch (err) {
            alert('Failed to resolve report');
        }
    };

    // New: Product handlers (mirror user)
    const handleCreateProduct = async () => {
        // Fetch users if not loaded (for dropdown)
        if (!users) {
            const usersData = await getAllUsers();
            setUsers(Array.isArray(usersData) ? usersData : []);
        }
        setSelectedProduct(null);
        setCreateProductFormData({
            title: '',
            price: 0,
            description: '',
            imageUrls: [],
            sellerId: '', // Selected from dropdown
            condition: ProductCondition.LikeNew,
            quantityAvailable: 1,
            categories: [],
            tags: [],
            specifications: {}
        });
        setIsCreatingProduct(true);
        setShowProductForm(true);
    };

    const handleEditProduct = async (id: number) => {
        console.log('Editing product with ID:', id);
        const productFromList = products?.find(p => p.id === id); // Preload from list
        if (productFromList) {
            setUpdateProductFormData({
                title: productFromList.title,
                price: productFromList.price,
                // Placeholders for full fields
                description: '',
                imageUrls: [],
                sellerId: '',
                isSold: false,
                condition: ProductCondition.LikeNew,
                status: productFromList.status, // From list
                quantityAvailable: 1,
                categories: [],
                tags: [],
                specifications: {}
            });
            setSelectedProduct({
                ...productFromList,
                // Placeholders
                description: '',
                imageUrls: [],
                sellerId: productFromList.sellerId ?? undefined,
                isSold: productFromList.isSold,
                condition: productFromList.condition,
                quantityAvailable: 1,
                categories: [],
                tags: [],
                averageRating: 0,
                reviewCount: 0,
                viewCount: productFromList.viewCount, // From list
                createdAt: '',
                updatedAt: undefined,
                specifications: {}
            });
            setIsCreatingProduct(false);
            setShowProductForm(true); // Open immediately with placeholders
            setIsProductLoading(true);
            // Async full fetch
            try {
                const productData = await getProductById(id);
                setSelectedProduct(productData);
                setUpdateProductFormData({
                    title: productData.title,
                    price: productData.price,
                    description: productData.description,
                    imageUrls: productData.imageUrls ?? [],
                    sellerId: productData.sellerId ?? '',
                    isSold: productData.isSold,
                    condition: productData.condition,
                    status: productData.status,
                    quantityAvailable: productData.quantityAvailable,
                    categories: productData.categories,
                    tags: productData.tags,
                    specifications: productData.specifications ?? {}
                });
            } catch (err) {
                alert('Failed to load full product details');
            } finally {
                setIsProductLoading(false);
            }
        }
    };

    const handleSaveProduct = async (payload: AdminCreateProductDto | AdminUpdateProductDto) => {
        let savedProduct;

        if (isCreatingProduct) {
            console.log('📤 Creating product with payload:', payload);
            savedProduct = await createProductAdmin(payload as AdminCreateProductDto);
        } else if (selectedProduct) {
            console.log('📤 Updating product with payload:', payload);
            savedProduct = await updateProductAdmin(selectedProduct.id, payload as AdminUpdateProductDto);
        }

        console.log('✅ Product saved successfully:', savedProduct);

        setShowProductForm(false);

        // Refresh list
        const productsData = await getAllItems();
        setProducts(Array.isArray(productsData) ? productsData : []);
    };

    // New: Order handlers (mirror)
    const handleCreateOrder = async () => {
        // Fetch users if not loaded (for dropdown)
        if (!users) {
            const usersData = await getAllUsers();
            setUsers(Array.isArray(usersData) ? usersData : []);
        }
        setSelectedOrder(null);
        setCreateOrderFormData({
            buyerId: '', // Selected from dropdown
            productId: 0,
            shopifyOrderId: '',
            total: 0,
            status: OrderStatus.Pending,
            paymentMethod: '',
            paymentStatus: PaymentStatus.Pending,
            shippingCost: 0,
            shippingMethod: '',
            trackingNumber: '',
            isDisputed: false,
            adminNotes: '',
            shippingStreetAddress: '',
            shippingCity: '',
            shippingPostalCode: '',
            shippingCountry: ''
        });
        setIsCreatingOrder(true);
        setShowOrderForm(true);
    };

    const handleEditOrder = async (id: number) => {
        console.log('Editing order with ID:', id);
        const orderFromList = orders?.find(o => o.id === id); // Preload from list
        if (orderFromList) {
            setUpdateOrderFormData({
                shopifyOrderId: orderFromList.shopifyOrderId,
                total: orderFromList.total,
                status: orderFromList.status as OrderStatus, // Cast string to enum
                // Placeholders for full fields
                shippedDate: '',
                paymentMethod: '',
                paymentStatus: PaymentStatus.Pending,
                shippingCost: 0,
                shippingMethod: '',
                trackingNumber: '',
                isDisputed: orderFromList.isDisputed,
                adminNotes: '',
                shippingStreetAddress: '',
                shippingCity: '',
                shippingPostalCode: '',
                shippingCountry: ''
            });
            setSelectedOrder({
                ...orderFromList,
                status: orderFromList.status as OrderStatus, // Cast
                // Placeholders
                orderDate: '',
                shippedDate: undefined,
                paymentMethod: undefined,
                paymentStatus: PaymentStatus.Pending, // Enum default
                shippingCost: 0,
                shippingMethod: undefined,
                trackingNumber: undefined,
                adminNotes: undefined,
                shippingStreetAddress: undefined,
                shippingCity: undefined,
                shippingPostalCode: undefined,
                shippingCountry: undefined
            });
            setIsCreatingOrder(false);
            setShowOrderForm(true); // Open immediately with placeholders
            setIsOrderLoading(true);
            // Async full fetch
            try {
                const orderData = await getAdminOrderById(id);
                setSelectedOrder(orderData);
                setUpdateOrderFormData({
                    shopifyOrderId: orderData.shopifyOrderId,
                    total: orderData.total,
                    status: orderData.status,
                    shippedDate: orderData.shippedDate ?? undefined,
                    paymentMethod: orderData.paymentMethod ?? '',
                    paymentStatus: orderData.paymentStatus,
                    shippingCost: orderData.shippingCost,
                    shippingMethod: orderData.shippingMethod ?? '',
                    trackingNumber: orderData.trackingNumber ?? '',
                    isDisputed: orderData.isDisputed,
                    adminNotes: orderData.adminNotes ?? '',
                    shippingStreetAddress: orderData.shippingStreetAddress ?? '',
                    shippingCity: orderData.shippingCity ?? '',
                    shippingPostalCode: orderData.shippingPostalCode ?? '',
                    shippingCountry: orderData.shippingCountry ?? ''
                });
            } catch (err) {
                alert('Failed to load full order details');
            } finally {
                setIsOrderLoading(false);
            }
        }
    };

    const handleSaveOrder = async () => {
        if (isCreatingOrder) {
            await createOrderAdmin(createOrderFormData);
        } else if (selectedOrder) {
            await updateOrderAdmin(selectedOrder.id, updateOrderFormData);
        }
        setShowOrderForm(false);
        // Refresh orders
        const ordersData = await getAllOrders();
        setOrders(Array.isArray(ordersData) ? ordersData : []);
    };

    const handleColorChange = (
        field: 'textColor' | 'backgroundColor' | 'accentColor' | 'highlightColor',
        value: string
    ) => {
        if (value === '' || hexRegex.test(value) || /^#[A-Fa-f0-9]{0,6}$/.test(value)) {
            setSettings((prev) => prev ? { ...prev, [field]: value.toUpperCase() } : prev);
        }
    };

    const handleImageUrlChange = (value: string) => {
        setSettings((prev) => prev ? { ...prev, backgroundImageUrl: value } : prev);
    };

    const handleWelcomeHeroImageUrlChange = (value: string) => {
        setSettings((prev) => (prev ? { ...prev, welcomeHeroImageUrl: value } : prev));
    };

    const handleWelcomeHowItWorksPathChange = (value: string) => {
        setSettings((prev) => (prev ? { ...prev, welcomeHowItWorksPath: value } : prev));
    };

    const handleContactChange = (field: 'contactEmail' | 'facebookUrl' | 'instagramUrl' | 'tikTokUrl' | 'pinterestUrl', value: string) => {
        setSettings((prev) => prev ? { ...prev, [field]: value } : prev);
    };

    const handleAllCategoriesIconChange = (value: string) => {
        setSettings((prev) => prev ? { ...prev, allCategoriesIcon: value } : prev);
    };

    const handleMarketplaceChange = (
        field: keyof Omit<MarketplaceEconomicsDto, 'id'>,
        value: string | number | boolean
    ) => {
        setEconomics((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleSaveEconomics = async () => {
        if (!economics) return;
        try {
            await updateMarketplaceEconomicsAdmin(economics);
            setEconomics(economics);
            alert('Economics settings saved.');
        } catch {
            alert('Save failed');
        }
    };

    const handleSaveSettings = async () => {
        if (!settings) return;
        const payload = {
            ...settings,
            highlightColor: settings.highlightColor || '#A67C52',
        };
        if (
            !hexRegex.test(payload.textColor) ||
            !hexRegex.test(payload.backgroundColor) ||
            !hexRegex.test(payload.accentColor) ||
            !hexRegex.test(payload.highlightColor)
        ) {
            alert('Invalid hex color');
            return;
        }
        if (payload.backgroundImageUrl && !/^https?:\/\/|^\/.*/.test(payload.backgroundImageUrl.trim())) {
            alert('Invalid background image URL');
            return;
        }
        if (
            payload.welcomeHeroImageUrl &&
            payload.welcomeHeroImageUrl.trim() &&
            !/^https?:\/\/|^\/.*/.test(payload.welcomeHeroImageUrl.trim())
        ) {
            alert('Invalid welcome banner URL (use https://… or a site-relative path)');
            return;
        }
        try {
            await updateSiteSettings(payload);
            setSettings(payload);
            try {
                localStorage.removeItem('siteTheme');
                localStorage.setItem('siteTheme_v2', JSON.stringify(payload));
            } catch {
                /* ignore quota */
            }
            alert('Saved! Open the webshop (/home) to see theme colors. Admin keeps its own indigo chrome.');
        } catch {
            alert('Save failed');
        }
    };

    const handleCreateSpec = () => {
        setSelectedSpec(null);
        setCreateSpecFormData({
            name: '',
            type: SpecificationType.String,
            isRequired: false,
            options: '',
        });
        setIsCreatingSpec(true);
        setShowSpecForm(true);
    };

    const handleEditSpec = async (id: number) => {
        const spec = specificationsList?.find(s => s.id === id);
        if (spec) {
            setUpdateSpecFormData({
                name: spec.name,
                type: spec.type,
                isRequired: spec.isRequired,
                options: spec.options ?? '',
            });
            setSelectedSpec(spec);
            setIsCreatingSpec(false);
            setShowSpecForm(true);
        }
    };

    const handleSaveSpec = async (): Promise<SpecificationDto | void> => {
        let savedSpec: SpecificationDto;
        if (isCreatingSpec) {
            savedSpec = await createSpecification(createSpecFormData);
        } else if (selectedSpec) {
            savedSpec = await updateSpecification(selectedSpec.id, updateSpecFormData);
        } else {
            return;
        }
        return savedSpec; // Let errors throw to modal for handling
    };

    const loadSpecifications = async () => {
        try {
            const specs = await getAllSpecifications();
            setSpecificationsList(specs);
        } catch (err) {
            console.error('Failed to load specifications:', err);
        }
    };

    const handleDeleteSpec = async (id: number) => {
        if (!window.confirm('Delete this specification?')) return;
        try {
            await deleteSpecification(id);
            setSpecificationsList(prev => prev ? prev.filter(s => s.id !== id) : null);
        } catch (err) {
            console.error('Delete failed');
        }
    };

    const loadSidebarFilters = async () => {
        const filters = await getAllAdminSidebarFilters();
        setSidebarFiltersList(Array.isArray(filters) ? filters : []);
    };

    const handleEditSidebarFilter = async (id: number) => {
        const filter = await getAdminSidebarFilterById(id);
        setEditingSidebarFilter(filter);
    };

    const handleSaveSidebarFilter = async (id: number, dto: UpdateSidebarFilterDto) => {
        await updateAdminSidebarFilter(id, dto);
        await loadSidebarFilters();
    };

    const handleSyncSidebarFilter = async (id: number) => {
        const updated = await syncAdminSidebarFilterOptions(id);
        if (editingSidebarFilter?.id === id) {
            setEditingSidebarFilter(updated);
        }
        await loadSidebarFilters();
        return updated;
    };

    const handleDeleteSidebarFilter = async (id: number) => {
        if (!window.confirm('Delete this sidebar filter?')) return;
        try {
            await deleteAdminSidebarFilter(id);
            setSidebarFiltersList((prev) => prev ? prev.filter((f) => f.id !== id) : null);
        } catch (err) {
            alert('Delete failed — built-in filters cannot be removed.');
        }
    };

    const handleCreateSidebarFilter = async () => {
        if (!newSidebarFilterSpecId) {
            alert('Select an enum specification first.');
            return;
        }
        try {
            const maxSort = sidebarFiltersList?.reduce((max, f) => Math.max(max, f.sortOrder), 0) ?? 0;
            await createAdminSidebarFilter({
                type: SidebarFilterType.Specification,
                specificationId: Number(newSidebarFilterSpecId),
                isEnabled: true,
                sortOrder: maxSort + 1,
            });
            setShowCreateSidebarFilter(false);
            setNewSidebarFilterSpecId('');
            await loadSidebarFilters();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to create sidebar filter.';
            alert(message);
        }
    };

    const handleCreateCategory = () => {
        setSelectedCategory(null);
        setCreateCategoryFormData({
            name: '',
            defaultPackageSizeBandCode: '',
            minPackageSizeBandCode: '',
        });
        setIsCreatingCategory(true);
        setShowCategoryForm(true);
    };

    const handleEditCategory = async (id: number) => {
        const cat = categoriesList?.find(c => c.id === id);
        if (cat) {
            setUpdateCategoryFormData({
                name: cat.name,
                defaultPackageSizeBandCode: cat.defaultPackageSizeBandCode ?? '',
                minPackageSizeBandCode: cat.minPackageSizeBandCode ?? '',
            });
            setSelectedCategory(cat);
            setIsCreatingCategory(false);
            setShowCategoryForm(true);
        }
    };

    const handleSaveCategory = async () => {
        try {
            if (isCreatingCategory) {
                await createCategory(createCategoryFormData);
            } else if (selectedCategory) {
                await updateCategory(selectedCategory.id, updateCategoryFormData);
            }
            setShowCategoryForm(false);
            // Refresh categories list
            const catsData = await getAllAdminCategories();
            setCategoriesList(Array.isArray(catsData) ? catsData : []);
            setAllCategories(Array.isArray(catsData) ? catsData : []); // Update allCategories
        } catch (err) {
            alert('Save failed');
        }
    };

    const handleDeleteCategory = async (id: number) => {
        if (!window.confirm('Delete this category?')) return;
        try {
            await deleteCategory(id);
            setCategoriesList(prev => prev ? prev.filter(c => c.id !== id) : null);
            // Refresh allCategories
            const catsData = await getAllAdminCategories();
            setAllCategories(Array.isArray(catsData) ? catsData : []);
        } catch (err) {
            console.error('Delete failed');
        }
    };

    const handleCreateChecklist = () => {
        setSelectedChecklist(null);
        setCreateChecklistFormData({
            names: {},
            phaseIds: [],
        });
        setIsCreatingChecklist(true);
        setShowChecklistForm(true);
    };

    const handleEditChecklist = async (id: number) => {
        const checklistFromList = checklistsList?.find(c => c.id === id);
        if (!checklistFromList) return;

        setIsCreatingChecklist(false);
        setSelectedChecklist(checklistFromList);

        // Fetch full template (including phases + per-language names)
        try {
            const fullTemplate = await getAdminChecklistTemplateById(id);

            setUpdateChecklistFormData({
                names: fullTemplate.names && Object.keys(fullTemplate.names).length
                    ? { ...fullTemplate.names }
                    : fullTemplate.name
                      ? { 'nl-be': fullTemplate.name }
                      : {},
                phaseIds: fullTemplate.phaseIds || [],
            });
        } catch (err) {
            console.error('Failed to load full template:', err);
            setUpdateChecklistFormData({
                names: checklistFromList.names && Object.keys(checklistFromList.names).length
                    ? { ...checklistFromList.names }
                    : checklistFromList.name
                      ? { 'nl-be': checklistFromList.name }
                      : {},
                phaseIds: [],
            });
        }

        setShowChecklistForm(true);
    };

    const handleSaveChecklist = async () => {
        try {
            if (isCreatingChecklist) {
                await AdmincreateChecklistTemplateAdmin(createChecklistFormData);
            } else if (selectedChecklist) {
                await AdminupdateChecklistTemplateAdmin(selectedChecklist.id, updateChecklistFormData);
            }
            setShowChecklistForm(false);
            // Refresh
            const data = await getAllAdminChecklistTemplates();
            setChecklistsList(Array.isArray(data) ? data : []);
        } catch (err) {
            alert('Save failed');
        }
    };

    const handleDeleteChecklist = async (id: number) => {
        if (!window.confirm('Delete this checklist template?')) return;
        try {
            await AdmindeleteChecklistTemplate(id);
            setChecklistsList(prev => prev ? prev.filter(c => c.id !== id) : null);
        } catch (err) {
            console.error('Delete failed');
        }
    };

    const handleSyncChecklists = async () => {
        setSyncChecklistsLoading(true);
        try {
            await AdminSyncChecklists();
            alert('Sync completed successfully!');

            // Refresh the list in admin
            const data = await getAllAdminChecklistTemplates();
            setChecklistsList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Sync failed:', err);
            alert('Sync failed. Please try again.');
        } finally {
            setSyncChecklistsLoading(false);
        }
    };

    const handleSubscribeWebhooks = async () => {
        setShopifyLoading(true);
        try {
            const result = await AdminSubscribeShopifyWebhooks();
            setShopifyStatus(result.subscriptions);
            setLastChecked(new Date().toLocaleTimeString());
            if (result.allGood) {
                alert('All Shopify webhooks are subscribed!');
            } else {
                alert('Some webhooks were missing and have been created.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to check/subscribe webhooks. Check console.');
        } finally {
            setShopifyLoading(false);
        }
    };

    const handleCreateTranslation = () => {
        const hasActiveLanguages = languages.some(l => l.isActive);
        if (!hasActiveLanguages) {
            alert("Please add at least one active language in the Setup tab first.");
            return;
        }

        setSelectedTranslation(null);
        setCreateTranslationFormData({
            key: '',
            description: '',
            translations: {}
        });
        setIsCreatingTranslation(true);
        setShowTranslationForm(true);
    };

    const handleEditTranslation = async (id: number) => {
        const translation = translationsList?.find(t => t.id === id);
        if (!translation) return;

        setSelectedTranslation(translation);
        setUpdateTranslationFormData({
            description: translation.description || '',
            translations: { ...translation.translations }   //  NEW
        });
        setIsCreatingTranslation(false);
        setShowTranslationForm(true);
    };

    const handleSaveTranslation = async () => {
        try {
            if (isCreatingTranslation) {
                const key = createTranslationFormData.key?.trim() ?? '';
                if (!key) {
                    alert('Translation key is required.');
                    return;
                }
                await createTranslationAdmin({
                    ...createTranslationFormData,
                    key,
                });
            } else if (selectedTranslation) {
                await updateTranslationAdmin(selectedTranslation.id, updateTranslationFormData);
            }
            setShowTranslationForm(false);

            // Refresh list
            const data = await getAllAdminTranslations();
            setTranslationsList(Array.isArray(data) ? data : []);
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.title ||
                err?.message ||
                'Save failed';
            alert(message);
        }
    };

    const handleDeleteTranslation = async (id: number) => {
        if (!window.confirm('Delete this translation?')) return;
        try {
            await deleteTranslationAdmin(id);
            setTranslationsList(prev => prev ? prev.filter(t => t.id !== id) : null);
        } catch (err) {
            console.error('Delete failed');
        }
    };

    // Language handlers
    const handleCreateLanguage = () => {
        setSelectedLanguage(null);
        setIsCreatingLanguage(true);
        setLanguageFormData({
            code: '',
            name: '',
            nativeName: '',
            flagEmoji: '',
            isActive: true,
            sortOrder: 0,
            translationKey: ''
        });
        setShowLanguageModal(true);
    };

    const handleEditLanguage = async (code: string): Promise<void> => {
        const language = languages.find(l => l.code === code);
        if (!language) return;

        setSelectedLanguage(language);
        setIsCreatingLanguage(false);
        setLanguageFormData({
            code: language.code,
            name: language.name,
            nativeName: language.nativeName || '',
            flagEmoji: language.flagEmoji || '',
            isActive: language.isActive,
            sortOrder: language.sortOrder ?? 0,
            translationKey: language.translationKey
        });
        setShowLanguageModal(true);
    };

    const handleDeleteLanguage = async (id: string | number) => {
        const code = String(id);
        if (!window.confirm('Delete this language?')) return;

        try {
            await deleteLanguageAdmin(code);
            await refreshLanguages();
        } catch (err) {
            alert('Delete failed');
        }
    };

    const handleSaveLanguage = async () => {
        try {
            if (isCreatingLanguage) {
                await createLanguageAdmin(languageFormData);
            } else if (selectedLanguage) {
                await updateLanguageAdmin(selectedLanguage.code, languageFormData);
            }
            setShowLanguageModal(false);
            await refreshLanguages();
        } catch (err) {
            alert('Save failed');
        }
    };

    const refreshLanguages = async () => {
        const langs = await getAllLanguagesAdmin();
        setLanguages(Array.isArray(langs) ? langs : []);
    };

    const getLoadingMessage = () => {
        switch (currentView) {
            case 'users': return 'Loading users...';
            case 'orders': return 'Loading orders...';
            case 'products': return 'Loading products...';
            case 'settings': return 'Loading settings...';
            case 'login-prompts': return null;
            case 'marketplace-economics': return 'Loading marketplace economics...';
            case 'specifications': return 'Loading specifications...';
            case 'categories': return 'Loading categories...';
            case 'checklists': return 'Loading checklists...';
            case 'partner-inquiries': return 'Loading partner inquiries...';
            case 'tickets': return 'Loading support tickets...';
            case 'status': return 'Loading status...';
            case 'translations': return 'Loading translations...';
            case 'email-templates': return null;
            case 'myrebaby-designer': return null;
            case 'setup': return 'Loading setup...';
            default: return 'Loading admin dashboard...';
        }
    };

    if (error) {
        return (
            <>
                <AdminHeader showMenuButton />
                {/* Keep top padding under fixed header — do not use sm:p-* which resets pt-20 */}
                <div className="px-4 pb-4 pt-20 text-red-500 sm:px-8 sm:pb-8">
                    {error}
                </div>
            </>
        );
    }

    return (
        <>
            <AdminHeader
                showMenuButton
                onMenuClick={() => setMobileNavOpen(true)}
            />
            <div className="min-h-screen lg:grid lg:grid-cols-[256px_1fr]">
                <aside className="bg-white shadow-lg lg:block">
                    <AdminSidebar
                        currentView={currentView}
                        onViewChange={setCurrentView}
                        mobileOpen={mobileNavOpen}
                        onMobileClose={() => setMobileNavOpen(false)}
                    />
                </aside>
                {/* pt-20 clears fixed AdminHeader; sm/lg only change horizontal/bottom padding */}
                <main className="min-h-screen overflow-y-auto bg-gray-50 px-4 pb-4 pt-20 sm:px-6 sm:pb-6 lg:px-8 lg:pb-8">
                    {loading ? (
                        <div className="flex h-[calc(100vh-5rem)] flex-col items-center justify-center">
                            <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-b-4 border-indigo-600"></div>
                            <p className="mt-6 text-xl font-medium text-gray-800 sm:text-2xl">{getLoadingMessage()}</p>
                        </div>
                    ) : (
                        <>
                            {currentView === 'email-templates' && (
                                <EmailTemplatesPanel />
                            )}
                            {currentView === 'myrebaby-designer' && (
                                <MyRebabyDesignerPanel />
                            )}
                            {currentView === 'meta-ad-catalog' && (
                                <MetaAdCatalogPanel />
                            )}
                            {currentView === 'translations' && translationsList !== null && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Translations</h1>

                                    {/* === GUARD: No active languages === */}
                                    {languages.filter(l => l.isActive).length === 0 && (
                                        <div className="mb-6 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 rounded">
                                            <p className="font-semibold">No active languages found.</p>
                                            <p className="text-sm mt-1">
                                                You need at least one active language before creating translations.
                                                Please go to the <strong>Setup</strong> tab and add languages first.
                                            </p>
                                        </div>
                                    )}

                                    {showTranslationForm ? (
                                        <TranslationModal
                                            isOpen={showTranslationForm}
                                            onClose={() => setShowTranslationForm(false)}
                                            isCreating={isCreatingTranslation}
                                            selectedTranslation={selectedTranslation}
                                            createFormData={createTranslationFormData}
                                            setCreateFormData={setCreateTranslationFormData}
                                            updateFormData={updateTranslationFormData}
                                            setUpdateFormData={setUpdateTranslationFormData}
                                            handleSaveTranslation={handleSaveTranslation}
                                            languages={languages}
                                            />
                                        ) : (
                                            <>
                                                <button
                                                    onClick={handleCreateTranslation}
                                                    disabled={languages.filter(l => l.isActive).length === 0}
                                                    className="mb-4 px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                >
                                                    Create Translation
                                                </button>
                                                <DataTable
                                                    data={translationsList}
                                                    onDelete={async (id) => await handleDeleteTranslation(Number(id))}
                                                    onEdit={(id: string | number) => handleEditTranslation(Number(id))}
                                                    idKey="id"
                                                    enableFilters={true}
                                                    filterResetKey={currentView}
                                                    renderNestedObjects={true}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                                {currentView === 'setup' && (
                                    <>
                                    <div className="flex justify-between items-center mb-6">
                                        <h1 className="text-3xl font-bold text-gray-800">Languages</h1>
                                        <button
                                            onClick={handleCreateLanguage}
                                            className="px-4 py-2 bg-blue-600 text-white rounded"
                                        >
                                            Add Language
                                        </button>
                                    </div>

                                    <DataTable
                                        data={languages}
                                        onEdit={handleEditLanguage}
                                        onDelete={handleDeleteLanguage}
                                        idKey="code"
                                        showIdKeyColumn={true}
                                    />

                                    <SetupPage
                                        isOpen={showLanguageModal}
                                        onClose={() => setShowLanguageModal(false)}
                                        isCreating={isCreatingLanguage}
                                        selectedLanguage={selectedLanguage}
                                        formData={languageFormData}
                                        setFormData={setLanguageFormData}
                                        handleSave={handleSaveLanguage}
                                    />
                                </>
                            )}
                            {currentView === 'status' && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Integrations</h1>
                                    {shopifyStatus && (
                                        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                                            {Object.values(shopifyStatus).every(v => v) ? (
                                                <>
                                                    <span className="text-2xl">✅</span>
                                                    <div>
                                                        <p className="font-semibold text-emerald-700">All Shopify webhooks are active</p>
                                                        <p className="text-sm text-emerald-600">You’re fully connected</p>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-2xl">⚠️</span>
                                                    <div>
                                                        <p className="font-semibold text-amber-700">Some webhooks are missing</p>
                                                        <p className="text-sm text-amber-600">Click the button to fix them</p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <div className="bg-white rounded-xl shadow p-8 max-w-2xl">
                                        <div className="flex items-center justify-between mb-6">
                                            <h2 className="text-xl font-semibold">Shopify Webhooks</h2>
                                            <button
                                                onClick={handleSubscribeWebhooks}
                                                disabled={shopifyLoading}
                                                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white font-medium rounded-lg flex items-center gap-2"
                                            >
                                                {shopifyLoading ? (
                                                    <>Checking...</>
                                                ) : (
                                                    <>🔄 Refresh & Subscribe Missing Webhooks</>
                                                )}
                                            </button>
                                        </div>

                                        {lastChecked && (
                                            <p className="text-sm text-gray-500 mb-4">Last checked: {lastChecked}</p>
                                        )}

                                        {shopifyStatus && (
                                            <div className="space-y-2">
                                                {Object.entries(shopifyStatus).map(([topic, isActive]) => (
                                                    <div key={topic} className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
                                                        <span className="font-mono text-sm">{topic}</span>
                                                        <span className={`inline-flex items-center gap-1 text-sm font-medium ${isActive ? 'text-emerald-600' : 'text-red-500'}`}>
                                                            {isActive ? '✅ Subscribed' : '❌ Missing'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {!shopifyStatus && !shopifyLoading && (
                                            <p className="text-gray-500 italic">Click the button above to check subscription status.</p>
                                        )}
                                    </div>
                                </>
                            )}
                            {currentView === 'users' && users && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Users</h1>
                                    {showUserForm ? (
                                        <UserModal
                                            isOpen={showUserForm}
                                            onClose={() => setShowUserForm(false)}
                                            isCreating={isCreating}
                                            selectedUser={selectedUser}
                                            createFormData={createFormData}
                                            setCreateFormData={setCreateFormData}
                                            updateFormData={updateFormData}
                                            setUpdateFormData={setUpdateFormData}
                                            newStrikeReason={newStrikeReason}
                                            setNewStrikeReason={setNewStrikeReason}
                                            handleSaveUser={handleSaveUser}
                                            handleAddStrike={handleAddStrike}
                                            handleRemoveStrike={handleRemoveStrike}
                                            handleBanUser={handleBanUser}
                                            handleUnbanUser={handleUnbanUser}
                                            handleSetEmailConfirmed={handleSetEmailConfirmed}
                                            handleResendEmailConfirmation={handleResendEmailConfirmation}
                                            additionalFields={additionalUserFields}
                                            isLoading={isUserLoading}
                                            handleResolveReport={handleResolveReport}
                                        />
                                    ) : (
                                        <>
                                            <button onClick={handleCreateUser} className="mb-4 px-4 py-2 bg-blue-500 text-white rounded">Create User</button>
                                            <DataTable
                                                data={users}
                                                onDelete={(id) => handleDelete('user', id)}
                                                onEdit={handleEditUser}
                                                idKey="id"
                                                enableFilters={true}
                                                filterResetKey={currentView}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            {currentView === 'products' && products && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Products</h1>
                                    {showProductForm ? (
                                        <ProductModal
                                            isOpen={showProductForm}
                                            onClose={() => setShowProductForm(false)}
                                            isCreating={isCreatingProduct}
                                            selectedProduct={selectedProduct}
                                            createFormData={createProductFormData}
                                            setCreateFormData={setCreateProductFormData}
                                            updateFormData={updateProductFormData}
                                            setUpdateFormData={setUpdateProductFormData}
                                            handleSaveProduct={handleSaveProduct}
                                            users={users ?? []} // Pass users for dropdown
                                            additionalFields={additionalProductFields}
                                            isLoading={isProductLoading}

                                        />
                                    ) : (
                                        <>
                                            <button onClick={handleCreateProduct} className="mb-4 px-4 py-2 bg-blue-500 text-white rounded">Create Product</button>
                                            <DataTable
                                                data={products}
                                                onDelete={(id) => handleDelete('product', id)}
                                                onEdit={(id: string | number) => handleEditProduct(Number(id))}
                                                idKey="id"
                                                enableFilters={true}
                                                filterResetKey={currentView}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            {currentView === 'orders' && orders && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Orders</h1>
                                    {showOrderForm ? (
                                        <OrderModal
                                            isOpen={showOrderForm}
                                            onClose={() => setShowOrderForm(false)}
                                            isCreating={isCreatingOrder}
                                            selectedOrder={selectedOrder}
                                            createFormData={createOrderFormData}
                                            setCreateFormData={setCreateOrderFormData}
                                            updateFormData={updateOrderFormData}
                                            setUpdateFormData={setUpdateOrderFormData}
                                            handleSaveOrder={handleSaveOrder}
                                            users={users ?? []} // Pass users for dropdown
                                            additionalFields={additionalOrderFields}
                                            isLoading={isOrderLoading}

                                        />
                                    ) : (
                                        <>
                                            <button onClick={handleCreateOrder} className="mb-4 px-4 py-2 bg-blue-500 text-white rounded">Create Order</button>
                                            <DataTable
                                                data={orders}
                                                onDelete={(id) => handleDelete('order', id)}
                                                onEdit={(id: string | number) => handleEditOrder(Number(id))}
                                                idKey="id"
                                                enableFilters={true}
                                                filterResetKey={currentView}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            {currentView === 'settings' && settings && (
                                <SiteSettingsSection
                                    settings={settings}
                                    onColorChange={handleColorChange}
                                    onImageUrlChange={handleImageUrlChange}
                                    onWelcomeHeroImageUrlChange={handleWelcomeHeroImageUrlChange}
                                    onWelcomeHowItWorksPathChange={handleWelcomeHowItWorksPathChange}
                                    onContactChange={handleContactChange}
                                    onAllCategoriesIconChange={handleAllCategoriesIconChange}
                                    onOpenPicker={setEditingField}
                                    editingField={editingField}
                                    tempColor={tempColor}
                                    setTempColor={setTempColor}
                                    onPickerSave={(color) => {
                                        if (editingField && settings) {
                                            setSettings({ ...settings, [editingField]: color.toUpperCase() });
                                        }
                                        setEditingField(null);
                                    }}
                                    onPickerCancel={() => setEditingField(null)}
                                    onSave={handleSaveSettings}
                                />
                            )}
                            {currentView === 'login-prompts' && (
                                <LoginPromptsSection />
                            )}
                            {currentView === 'marketplace-economics' && economics && (
                                <MarketplaceEconomicsSection
                                    economics={economics}
                                    onChange={handleMarketplaceChange}
                                    onSave={handleSaveEconomics}
                                />
                            )}
                            {currentView === 'pickup-locations' && <PickupLocationsSection />}
                            {/* New: Categories section */}
                            {currentView === 'categories' && categoriesList && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Categories</h1>
                                    {showCategoryForm ? (
                                        <CategoryModal
                                            isOpen={showCategoryForm}
                                            onClose={() => setShowCategoryForm(false)}
                                            isCreating={isCreatingCategory}
                                            selectedCategory={selectedCategory}
                                            createFormData={createCategoryFormData}
                                            setCreateFormData={setCreateCategoryFormData}
                                            updateFormData={updateCategoryFormData}
                                            setUpdateFormData={setUpdateCategoryFormData}
                                            handleSaveCategory={handleSaveCategory}
                                        />
                                    ) : (
                                        <>
                                            <button onClick={handleCreateCategory} className="mb-4 px-4 py-2 bg-blue-500 text-white rounded">Create Category</button>
                                            <DataTable
                                                data={categoriesList}
                                                onDelete={(id) => handleDeleteCategory(Number(id))}
                                                onEdit={(id: string | number) => handleEditCategory(Number(id))}
                                                idKey="id"
                                                enableFilters={true}
                                                filterResetKey={currentView}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            {currentView === 'sidebar-filters' && sidebarFiltersList && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Sidebar Filters</h1>
                                    {editingSidebarFilter && (
                                        <SidebarFilterModal
                                            filter={editingSidebarFilter}
                                            enumSpecifications={specificationsList?.filter((s) => s.type === SpecificationType.Enum) ?? []}
                                            onClose={() => setEditingSidebarFilter(null)}
                                            onSave={handleSaveSidebarFilter}
                                            onSync={handleSyncSidebarFilter}
                                        />
                                    )}
                                    {showCreateSidebarFilter && (
                                        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                            <h3 className="mb-3 text-sm font-semibold text-gray-800">Add specification filter</h3>
                                            <div className="flex flex-wrap items-end gap-3">
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Enum specification</label>
                                                    <select
                                                        className="rounded border border-gray-300 px-3 py-2 text-sm"
                                                        value={newSidebarFilterSpecId}
                                                        onChange={(e) => setNewSidebarFilterSpecId(e.target.value ? Number(e.target.value) : '')}
                                                    >
                                                        <option value="">Select…</option>
                                                        {(specificationsList ?? [])
                                                            .filter((s) => s.type === SpecificationType.Enum)
                                                            .filter((s) => !sidebarFiltersList.some((f) => f.specificationId === s.id))
                                                            .map((s) => (
                                                                <option key={s.id} value={s.id}>{s.name}</option>
                                                            ))}
                                                    </select>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleCreateSidebarFilter}
                                                    className="rounded bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600"
                                                >
                                                    Create
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setShowCreateSidebarFilter(false); setNewSidebarFilterSpecId(''); }}
                                                    className="rounded px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <p className="mb-4 text-sm text-gray-600">
                                        Price, condition, and growth-phase filters are created automatically on first load.
                                        Use the button below to link an enum specification (e.g. season, age range).
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateSidebarFilter(true)}
                                        className="mb-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                                    >
                                        Add specification filter
                                    </button>
                                    <DataTable
                                        data={sidebarFiltersList}
                                        onDelete={(id) => handleDeleteSidebarFilter(Number(id))}
                                        onEdit={(id: string | number) => handleEditSidebarFilter(Number(id))}
                                        idKey="id"
                                        enableFilters={true}
                                        filterResetKey={currentView}
                                    />
                                </>
                            )}
                            {currentView === 'specifications' && specificationsList && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Specifications</h1>
                                    {showSpecForm ? (
                                        <SpecificationModal
                                            isOpen={showSpecForm}
                                            onClose={() => {
                                                setShowSpecForm(false);
                                                loadSpecifications(); // Reload full list to include assigned categories
                                            }}
                                            isCreating={isCreatingSpec}
                                            selectedSpec={selectedSpec}
                                            createFormData={createSpecFormData}
                                            setCreateFormData={setCreateSpecFormData}
                                            updateFormData={updateSpecFormData}
                                            setUpdateFormData={setUpdateSpecFormData}
                                            handleSaveSpec={handleSaveSpec}
                                            categories={allCategories}
                                        />
                                    ) : (
                                        <>
                                            <button onClick={handleCreateSpec} className="mb-4 px-4 py-2 bg-blue-500 text-white rounded">Create Specification</button>
                                            <DataTable
                                                data={specificationsList}
                                                onDelete={(id) => handleDeleteSpec(Number(id))}
                                                onEdit={(id: string | number) => handleEditSpec(Number(id))}
                                                idKey="id"
                                                enableFilters={true}
                                                filterResetKey={currentView}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            {currentView === 'partner-inquiries' && partnerInquiries && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Partner Inquiries</h1>
                                    <DataTable
                                        data={partnerInquiries}
                                        onDelete={() => {}}
                                        idKey="id"
                                        enableFilters={true}
                                        filterResetKey={currentView}
                                        excludeColumns={[]}
                                    />
                                </>
                            )}
                            {currentView === 'tickets' && tickets && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Support tickets</h1>
                                    <p className="mb-4 text-sm text-gray-600">
                                        Order issues and helpdesk messages from shop users. Change status as you work them.
                                    </p>
                                    <DataTable
                                        data={tickets}
                                        onDelete={() => {}}
                                        idKey="id"
                                        enableFilters={true}
                                        filterResetKey={currentView}
                                        excludeColumns={['userId'] as (keyof AdminTicketListDto)[]}
                                        customCellRenderers={{
                                            status: (value, row) => {
                                                const current = String(value ?? 'Open');
                                                const busy = ticketStatusUpdatingId === row.id;
                                                return (
                                                    <select
                                                        className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-50"
                                                        value={current}
                                                        disabled={busy}
                                                        onChange={async (e) => {
                                                            const next = e.target.value;
                                                            if (next === current) return;
                                                            setTicketStatusUpdatingId(row.id);
                                                            try {
                                                                await updateAdminTicketStatus(row.id, next);
                                                                setTickets((prev) =>
                                                                    (prev ?? []).map((t) =>
                                                                        t.id === row.id ? { ...t, status: next } : t
                                                                    )
                                                                );
                                                            } catch {
                                                                alert('Failed to update ticket status');
                                                            } finally {
                                                                setTicketStatusUpdatingId(null);
                                                            }
                                                        }}
                                                    >
                                                        <option value="Open">Open</option>
                                                        <option value="InProgress">In progress</option>
                                                        <option value="Closed">Closed</option>
                                                    </select>
                                                );
                                            },
                                            message: (value) => {
                                                const text = String(value ?? '');
                                                return (
                                                    <span className="block max-w-xs truncate" title={text}>
                                                        {text}
                                                    </span>
                                                );
                                            },
                                        }}
                                    />
                                </>
                            )}
                            {currentView === 'checklists' && checklistsList && (
                                <>
                                    <h1 className="text-3xl font-bold text-gray-800 mb-8">Checklist Templates</h1>
                                    {showChecklistForm ? (
                                        <ChecklistModal
                                            isOpen={showChecklistForm}
                                            onClose={() => setShowChecklistForm(false)}
                                            isCreating={isCreatingChecklist}
                                            selectedTemplate={selectedChecklist}
                                            createFormData={createChecklistFormData}
                                            setCreateFormData={setCreateChecklistFormData}
                                            updateFormData={updateChecklistFormData}
                                            setUpdateFormData={setUpdateChecklistFormData}
                                            handleSaveChecklist={handleSaveChecklist}
                                        />
                                    ) : (
                                        <>
                                            <button onClick={handleCreateChecklist} className="mb-4 px-4 py-2 bg-blue-500 text-white rounded">Create Checklist Template</button>
                                            <button
                                                onClick={() => void handleSyncChecklists()}
                                                disabled={syncChecklistsLoading}
                                                className="mb-4 ml-2 px-4 py-2 bg-green-500 text-white rounded"
                                            >
                                                {syncChecklistsLoading ? 'Syncing...' : 'Sync checklists'}
                                            </button>
                                            <DataTable
                                                data={checklistsList}
                                                onDelete={(id) => handleDeleteChecklist(Number(id))}
                                                onEdit={(id: string | number) => handleEditChecklist(Number(id))}
                                                idKey="id"
                                                enableFilters={true}
                                                filterResetKey={currentView}
                                            />
                                            <h2 className="text-2xl font-bold text-gray-800 mt-12 mb-4">Growth Roadmap Phases</h2>
                                            {editingPhase && (
                                                <ChecklistPhaseIconModal
                                                    phase={editingPhase}
                                                    onClose={() => setEditingPhase(null)}
                                                    onSave={async (icon) => {
                                                        const updated = await updateChecklistPhaseIcon(editingPhase.id, icon);
                                                        setChecklistPhases((prev) =>
                                                            prev?.map((p) => (p.id === updated.id ? updated : p)) ?? null
                                                        );
                                                    }}
                                                />
                                            )}
                                            {checklistPhases && (
                                                <DataTable
                                                    data={checklistPhases}
                                                    onDelete={() => {}}
                                                    onEdit={(id: string | number) => {
                                                        const phase = checklistPhases.find((p) => p.id === Number(id));
                                                        if (phase) setEditingPhase(phase);
                                                    }}
                                                    idKey="id"
                                                    enableFilters={true}
                                                    filterResetKey={`${currentView}-phases`}
                                                />
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </main>
            </div>
        </>
    );
};

export default AdminDashboard;